import { Button, Stack, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import React, { useState } from 'react';
import { Translate } from 'react-localize-redux';

import Modal from '../../../components/general/modal';
import { SnackbarTypeSeverity } from '../../../constants/types';
import { HOSPITAL_BILLING_PATIENT } from '../../../routes/urls';
import { updateSurgeryRequestStatusService } from '../../../services/request';
import { IRequest, RequestStatus, SurgeryStatus } from './types';

export interface SurgeryStatusActionCoreProps {
    /** The transitions the API will accept next (SHER-236) — presented, never computed here. */
    nextStatuses: SurgeryStatus[];
    /** Whether the invoice still has a balance; the way to billing must stand on its own. */
    owesPayment: boolean;
    billingUrl: string;
    loading: boolean;
    onConfirm: (status: SurgeryStatus) => void | Promise<boolean | void>;
}

/**
 * SHER-1062 — the surgical "Prochaine action" of the request-management panel.
 *
 * Presentational core of what RequestSingle's RequestActions used to render, lifted out so the
 * panel can be designed in the catalogue. Everything the server decides stays decided by the
 * server: which transitions exist (they depend on the linked bill and the VPA history, which
 * this app does not hold) and whether money is owed.
 *
 * Transitions are OUTLINED, not the filled primary button the other modules get: the API often
 * offers two outcomes at once (VPA apte / non apte), and filling one would present a clinical
 * decision as the default.
 */
export const SurgeryStatusActionCore: React.FC<SurgeryStatusActionCoreProps> = ({ nextStatuses, owesPayment, billingUrl, loading, onConfirm }) => {
    const [pending, setPending] = useState<SurgeryStatus | null>(null);

    if (nextStatuses.length === 0 && !owesPayment) {
        return null;
    }

    const handleConfirm = async () => {
        await onConfirm(pending as SurgeryStatus);
        setPending(null);
    };

    return (
        <>
            <Modal
                open={pending !== null}
                closeModal={() => setPending(null)}
                confirmAction={handleConfirm}
                disabled={loading}
                title={<Translate id="pages.hospital.services.request.status_update.title" />}
            >
                <Typography variant="body1">
                    <Translate id="pages.hospital.services.request.status_update.modal" />{' '}
                    {pending !== null &&
                        <strong><Translate id={`pages.hospital.services.request_status.${RequestStatus[pending]}`} /></strong>
                    }
                </Typography>
            </Modal>
            <Stack direction="row" flexWrap="wrap" useFlexGap sx={{ gap: '8px' }}>
                {nextStatuses.map((status) => (
                    <Button
                        key={status}
                        variant="outlined"
                        endIcon={<ArrowForwardIcon />}
                        disabled={loading}
                        onClick={() => setPending(status)}
                        data-testid={`surgery-next-status-${status}`}
                    >
                        <Translate id={`pages.hospital.services.request_status.${RequestStatus[status]}`} />
                    </Button>
                ))}
                {owesPayment &&
                    <Button
                        variant="text"
                        endIcon={<OpenInNewIcon />}
                        onClick={() => window.open(billingUrl, '_blank')}
                        data-testid="surgery-go-to-billing"
                    >
                        <Translate id="pages.hospital.services.request.status_update.go_to_billing" />
                    </Button>
                }
            </Stack>
        </>
    );
};

interface SurgeryStatusActionProps {
    request: IRequest;
    uuidInvestigation: string;
    setShowSnackbar: (snackbar: { show: boolean; message?: string; severity?: SnackbarTypeSeverity }) => void;
}

/**
 * Container: wires the surgical transition endpoint to the core. The outcome handling is the
 * one RequestActions had (SHER-236), kept as is: a success snackbar — RequestSingle reloads the
 * request when that snackbar closes — and errorCode 1 as the one refusal with its own message.
 * Not useRequestMutation: this endpoint's error codes are not the modify/cancel ones.
 */
const SurgeryStatusAction: React.FC<SurgeryStatusActionProps> = ({ request, uuidInvestigation, setShowSnackbar }) => {
    const [loading, setLoading] = useState(false);

    const handleConfirm = async (status: SurgeryStatus) => {
        setLoading(true);
        try {
            await updateSurgeryRequestStatusService(uuidInvestigation, request.id, status);
            setShowSnackbar({ show: true, message: 'pages.hospital.services.request.status_updated', severity: SnackbarTypeSeverity.SUCCESS });
        } catch (error: any) {
            // A refusal (4xx) rejects with the body; a 5xx or network failure with the axios error.
            const errorCode = error?.errorCode ?? error?.response?.data?.errorCode;
            setShowSnackbar({
                show: true,
                message: errorCode === 1 ? 'pages.hospital.services.request.error_code_1' : 'general.error',
                severity: SnackbarTypeSeverity.ERROR,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <SurgeryStatusActionCore
            nextStatuses={request.nextStatuses ?? []}
            owesPayment={request.owesPayment ?? false}
            billingUrl={HOSPITAL_BILLING_PATIENT.replace(':uuidPatient', request.requestsServiceInvestigation[0].patientInvestigation.uuid)}
            loading={loading}
            onConfirm={handleConfirm}
        />
    );
};

export default SurgeryStatusAction;
